import React, { useMemo } from "react";
import { colors2, Container, OKELink, PaddingTopBottom20, SkeletonLoader, UI_BODY_SEMIBOLD_SM, UI_TAG, SwitchSingle, icons, TooltipV2 } from "oolib";
import { Sort } from "../../../../../pageTpls/ListingPage/newComps/ActionsPanel/comps/Sort";
import { StyledSortButton } from "../../../../../pageTpls/ListingPage/newComps/ActionsPanel/styled";
import { ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";
import { MasonryCardGridShell } from "../MasonryCardGridShell";
import { CardAnnoExplorer } from "../CardAnnoExplorer";
import { TableAnnoExplorer } from "../TableAnnoExplorer";
import { getAnnoExplorerConfigsForThisTenant } from "../../utils/getAnnoExplorerConfigsForThisTenant";
import { applyTagsWhitelistConfig } from "../../utils/applyTagsWhitelistConfig";
import { ColorCodeDisplay, StyledStatusWrapper, StyledWrapper } from "../ColorCodeDisplay";
import { Summary, useSummary, SummaryV2, useAnnoSummaryV2 } from "../Summary";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useAnnotationBarGraphData } from "../../utils/useAnnotationBarGraphData";
import { useAnnoExplorerTaxonomyTabs } from "../../utils/useAnnoExplorerTaxonomyTabs";
import { BarGraphCollection } from "@/components/dataviz/BarGraphCollection";
import { useStretchDivToRemainingHeight } from "@/utils/customHooks";
import { useDynamicLayoutHeight } from "@/components/analysisModules/InsightMatrix/utils/useDynamicLayoutHeight";


export const ListingModuleAnnotationExplorer = ({
  data,
  dataFetchStatus,
  dataFetchError,
  isFetchingNextPage,
  observerRef,
  breakDown, 
  setBreakDown,
  colorBreakdownStats,
  isMobile,
  activeColorFilter,
  activeFilters,
  selectedTPLsArrayOptions,
  dataSource,
  annotationExplorerAIConfigs,
  searchTerm,
  csvExporting,
  handleDownloadCsv,
  countDataStatus,
  countData,
  defaultSortOptions,
  activeSort,
  setActiveSort
}) => {
  const annoExplorerConfigs = getAnnoExplorerConfigsForThisTenant()
  const disableCSVExport = annoExplorerConfigs?.disableCSVExport;
  const enableBarGraph = !annoExplorerConfigs?.tempDisableBarGraph;
  const disableSummarizeButton = annoExplorerConfigs?.disableSummarizeButton

  const activeSortValue = useMemo(() => defaultSortOptions?.filter(item => item.value === activeSort), [activeSort, defaultSortOptions])?.[0]

  // V2 (ReAct-agent-based summary) is gated by FEATURES_DISABLED_ON_PROD.
  // On prod, the feature is disabled by default → fall back to the old
  // button-gated summary. On staging/dev, V2 is on → auto-fetches.
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } = useGetQueryData("platformConfigs") || {};
  const isProd = import.meta.env.VITE_ENV === "prod";
  const useV2Summary = !(isProd && disabledFeatures.includes("annoSummaryV2"));

  // V2 hook (auto-fetch; disabled when feature is off or summarize is per-tenant disabled)
  const contentTypeNames = useMemo(
    () => (selectedTPLsArrayOptions || []).map((o) => o.value).filter(Boolean),
    [selectedTPLsArrayOptions]
  );
  const {
    summary: summaryV2,
    shortSummary: summaryV2Short,
    empty: summaryEmpty,
    hasTriggered: summaryHasTriggered,
    loading: summaryLoading,
    isRefreshing: summaryRefreshing,
    generate: generateSummary,
    refresh: refreshSummary,
  } = useAnnoSummaryV2({
    activeFilters,
    contentTypes: contentTypeNames,
    enabled: useV2Summary && !disableSummarizeButton,
  });

  // Old summary hook — always called (rules of hooks) but only rendered on prod.
  // The mutation doesn't fire until the button is clicked, so calling it is free.
  const {
    summary: summaryOld,
    generateSummaryStatus,
    handleOnClickGenerateSummary,
    buttonDisabled: summarizeButtonDisabled,
    buttonText: summarizeButtonText,
    tooltipText: summarizeTooltipText,
  } = useSummary({
    activeFilters,
    dataSource: "annotations",
    totalCount: countData?.count,
    searchTerm,
  });

  const {
    data: barGraphData,
    status: barGraphStatus,
    error: barGraphError,
    isLoading: barGraphLoading,
  } = useAnnotationBarGraphData(
    enableBarGraph,
    activeFilters,
    selectedTPLsArrayOptions,
  );

  const {
    enabled: hasExperimentalTopicModelling,
    filteredBarGraphData,
  } = useAnnoExplorerTaxonomyTabs(barGraphData);

  const showBarGraphLoader = (!data && enableBarGraph) || (enableBarGraph && barGraphStatus === "loading");
  const isBarGraphEnabled = !annoExplorerConfigs?.tempDisableBarGraph;
  const isDataLoading = dataFetchStatus === "loading";
  const showTotalCountLoader = countDataStatus === 'loading' && !annoExplorerConfigs?.disableTotalCount;
  const canShowTotalCount = countData?.count && !annoExplorerConfigs?.disableTotalCount && !breakDown?.activeColorConfig?.tags?.length;
  const isCSVExportEnabled = !disableCSVExport;
  const hasDataLoaded = dataFetchStatus !== "loading";
  const {gridHeightCSS, parentWrapperRef} = useDynamicLayoutHeight()

  // Drop annotations with no text content — they crash LexicalComposer.
  const filteredData = useMemo(
    () => (Array.isArray(data) ? data.filter(d => d?.annotations?.fragment?.allText?.trim()) : data),
    [data]
  );

  return (
    <div
      ref={parentWrapperRef}
      style={{
        background: colors2.grey5,
        // padding: "2rem",
        minHeight: gridHeightCSS,
      }}
    >
        {/* Top bar-graph is replaced by the right-side panel when
            aiAnnotation.mode is non-legacy; AnnotationExplorerVizPanel
            renders the same data per-subTheme in a vertical layout. */}
        {!hasExperimentalTopicModelling && (
          showBarGraphLoader ?
          <SkeletonLoader
            style={{
              width: "100%",
              height: "250px",
            }}
          />
          :
          isBarGraphEnabled && barGraphData?.length > 0 && (
            <div style={{ paddingLeft: "4rem", background: "white", borderRadius: "0.8rem" }}>
              <BarGraphCollection
                data={filteredBarGraphData}
                valuePath="value"
                labelPath="display"
                groupBy="category"
                totalDataCount={countData?.count}
                // debug={true}
              />
            </div>
          )
        )}

        {/* {hasDataLoaded 
          && 
          <ColorCodeDisplay
            isMobile={isMobile} 
            breakDown={breakDown} 
            setBreakDown={setBreakDown}
            colorBreakdownStats={colorBreakdownStats}
            countData?.count={countData?.count}
            activeColorFilter={activeColorFilter} 
          />
        }  */}

      <div>
        {/* {  
            (canShowTotalCount || (isCSVExportEnabled && data?.length > 0) ) ? (
              <StyledWrapper isMobile={isMobile}>
                <StyledStatusWrapper>
                  {canShowTotalCount ?
                  (showTotalCountLoader 
                  ? <UI_BODY_SEMIBOLD_SM>...</UI_BODY_SEMIBOLD_SM>
                  : <UI_BODY_SEMIBOLD_SM>{`Annotations Showing: ${countData?.count || 0}`}</UI_BODY_SEMIBOLD_SM> )
                : <div/>}
                  {
              isCSVExportEnabled && data?.length > 0 &&
                <UI_BODY_SEMIBOLD_SM>
                <OKELink
                  icon="DownloadSimple"
                  onClick={handleDownloadCsv}
                  disabled={csvExporting}
                  style={{ justifySelf: "end" }}
                  invertUnderline
                >
                  {csvExporting ? "Exporting..." : "Download as CSV"}
                </OKELink>
                </UI_BODY_SEMIBOLD_SM>
             }
                </StyledStatusWrapper>
              </StyledWrapper>
            ) : null
        } */}
        
        {(canShowTotalCount || (isCSVExportEnabled && data?.length > 0)) ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.4rem 2rem",
          borderTop: `1px solid ${colors2.grey10}`,
          borderBottom: `1px solid ${colors2.grey10}`,
          background: colors2.white,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1.4rem" }}>
            <UI_BODY_SEMIBOLD_SM>
              {data?.length || 0}{!annoExplorerConfigs?.disableTotalCount && `/${countData?.count || 0}`} Annotations
            </UI_BODY_SEMIBOLD_SM>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <ButtonTertiaryCompact
                icon="DownloadSimple"
                onClick={handleDownloadCsv}
                disabled={csvExporting}
              >
                {csvExporting ? "Exporting..." : "Export CSV"}
              </ButtonTertiaryCompact>
              {/* Old "Summarize" button — only shown when V2 is disabled (prod fallback) */}
              {!useV2Summary && !disableSummarizeButton && !summaryOld && (
                summarizeTooltipText ? (
                  <TooltipV2 text={summarizeTooltipText} placement="bottom">
                    <ButtonTertiaryCompact
                      icon="Sparkle"
                      onClick={handleOnClickGenerateSummary}
                      disabled={summarizeButtonDisabled}
                    >
                      {summarizeButtonText}
                    </ButtonTertiaryCompact>
                  </TooltipV2>
                ) : (
                  <ButtonTertiaryCompact
                    icon="Sparkle"
                    onClick={handleOnClickGenerateSummary}
                    disabled={summarizeButtonDisabled}
                  >
                    {summarizeButtonText}
                  </ButtonTertiaryCompact>
                )
              )}
            </div>
          </div>
          <Sort
            genCustomSelectComp={() => (
              <StyledSortButton>
                <icons.SortDescending size={16} weight="bold" />
                <UI_BODY_SEMIBOLD_SM>{`Sort by: ${activeSort?.display || activeSortValue?.display}`}</UI_BODY_SEMIBOLD_SM>
              </StyledSortButton>
            )}
            usedIn="Annotation Explorer"
            saveValueAsString
            sortConfig={defaultSortOptions}
            activeSort={activeSort}
            setActiveSort={setActiveSort}
          />
        </div>
        ) : null}
        
        {!disableSummarizeButton && useV2Summary && (
          <SummaryV2
            summary={summaryV2}
            shortSummary={summaryV2Short}
            empty={summaryEmpty}
            hasTriggered={summaryHasTriggered}
            loading={summaryLoading}
            isRefreshing={summaryRefreshing}
            onGenerate={generateSummary}
            onRefresh={refreshSummary}
          />
        )}

        {!useV2Summary && summaryOld && (
          <Summary
            summary={summaryOld}
            onRefresh={handleOnClickGenerateSummary}
            isRefreshing={generateSummaryStatus === "loading"}
          />
        )}


        <div style={{ padding: "2rem" }}>
          <TableAnnoExplorer
            data={filteredData}
            dataFetchStatus={dataFetchStatus}
            activeSort={activeSort}
            setActiveSort={setActiveSort}
          />
          {/* Single infinite-scroll sentinel. observerRef is null'd by the
              parent when !hasNextPage. */}
          <div ref={observerRef} style={{ width: "100%", height: "1px" }} />
        </div>
      </div>
    </div>
  );
};
